(function () {
  const DEBUG_SOURCE = 'upcart-integration';

  function getDebugState(extra) {
    const drawer = document.querySelector('cart-drawer');
    return Object.assign({
      source: DEBUG_SOURCE,
      href: window.location.href,
      pathname: window.location.pathname,
      shopifyRoot: window.Shopify?.routes?.root,
      bodyClass: document.body?.className,
      bodyOverflowStyle: document.body?.style?.overflow,
      bodyOverflowComputed: document.body ? window.getComputedStyle(document.body).overflow : null,
      upcartOpenCartType: typeof window.upcartOpenCart,
      upcartRefreshCartType: typeof window.upcartRefreshCart,
      hasUpcartSettings: Boolean(window.upcartSettings),
      upcartCartIsEnabled: window.upcartSettings?.upcartEditorSettings?.cartIsEnabled,
      hasThemeDrawer: Boolean(drawer),
      themeDrawerClass: drawer?.className || null,
      themeDrawerHidden: drawer?.getAttribute('aria-hidden') || null,
      hasPompeiiOpenUpcart: typeof window.pompeiiOpenUpcart === 'function',
    }, extra || {});
  }

  function debugLog(step, extra) {
    console.log('[DLM Upcart Debug]', step, getDebugState(extra));
  }

  debugLog('script:start', {
    alreadyInitialized: Boolean(window.__pompeiiUpcartIntegrationInit),
    scripts: Array.from(document.scripts)
      .map((script) => script.src)
      .filter((src) => /upcart|cart-drawer/i.test(src)),
  });

  if (window.__pompeiiUpcartIntegrationInit) {
    debugLog('script:skip-already-initialized');
    return;
  }
  window.__pompeiiUpcartIntegrationInit = true;
  let isOpeningUpcart = false;

  function cartRoute(path) {
    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    return `${normalizedRoot}${String(path || '').replace(/^\/+/, '')}`;
  }

  function isCartLink(link) {
    if (!link || !link.getAttribute('href')) return false;

    try {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      return url.origin === window.location.origin && /\/cart\/?$/.test(url.pathname);
    } catch {
      return false;
    }
  }

  function shouldUseUpcart() {
    const result = Boolean(
      typeof window.upcartOpenCart === 'function' ||
        (window.upcartSettings?.upcartSettings && window.upcartSettings?.upcartEditorSettings?.cartIsEnabled !== false)
    );

    debugLog('shouldUseUpcart', { result });
    return result;
  }

  function clearThemeCartState() {
    debugLog('clearThemeCartState:before');

    document.body.classList.remove('cart-drawer-open');
    document.body.style.overflow = '';

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    }

    debugLog('clearThemeCartState:after');
  }

  function waitForUpcartApi(timeout) {
    debugLog('waitForUpcartApi:start', { timeout });

    if (typeof window.upcartOpenCart === 'function') {
      debugLog('waitForUpcartApi:ready-immediate');
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        if (typeof window.upcartOpenCart === 'function') {
          window.clearInterval(timer);
          debugLog('waitForUpcartApi:ready-after-wait', { elapsed: Date.now() - startedAt });
          resolve(true);
          return;
        }

        if (Date.now() - startedAt >= timeout) {
          window.clearInterval(timer);
          debugLog('waitForUpcartApi:timeout', { elapsed: Date.now() - startedAt });
          resolve(false);
        }
      }, 50);
    });
  }

  async function openUpcart() {
    debugLog('openUpcart:start', { isOpeningUpcart });

    if (isOpeningUpcart) {
      debugLog('openUpcart:skip-already-opening');
      return;
    }

    isOpeningUpcart = true;
    clearThemeCartState();

    try {
      const ready = await waitForUpcartApi(4000);
      if (!ready) {
        debugLog('openUpcart:fallback-to-cart-page');
        window.location.href = cartRoute('cart');
        return;
      }

      if (typeof window.upcartRefreshCart === 'function') {
        debugLog('openUpcart:refresh-start');
        await window.upcartRefreshCart()
          .then(() => debugLog('openUpcart:refresh-success'))
          .catch((error) => debugLog('openUpcart:refresh-error', { error: String(error) }));
      }

      clearThemeCartState();
      debugLog('openUpcart:calling-window-upcartOpenCart');
      window.upcartOpenCart();
      debugLog('openUpcart:called-window-upcartOpenCart');
    } catch (error) {
      debugLog('openUpcart:error', { error: String(error), stack: error?.stack });
    } finally {
      isOpeningUpcart = false;
      debugLog('openUpcart:finish');
    }
  }

  function initThemeDrawerGuard() {
    if (!document.body) {
      debugLog('themeDrawerGuard:no-body');
      return;
    }

    debugLog('themeDrawerGuard:init');

    const observer = new MutationObserver(() => {
      debugLog('themeDrawerGuard:body-class-change');

      if (document.body.classList.contains('cart-drawer-open') && shouldUseUpcart()) {
        debugLog('themeDrawerGuard:theme-drawer-open-detected');
        void openUpcart();
      }
    });

    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cart-toggle], a[href]');
    if (!opener) return;
    if (!opener.matches('[data-cart-toggle]') && !isCartLink(opener)) return;

    debugLog('click:capture-cart-opener', {
      openerTag: opener.tagName,
      openerHref: opener.getAttribute('href'),
      openerClass: opener.className,
      hasDataCartToggle: opener.matches('[data-cart-toggle]'),
      defaultPreventedBefore: event.defaultPrevented,
    });

    if (!shouldUseUpcart()) {
      debugLog('click:capture-skip-upcart-disabled');
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    debugLog('click:capture-open-upcart');
    void openUpcart();
  }, true);

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;

    debugLog('submit:capture-cart-add', {
      action: form.getAttribute('action'),
      method: form.getAttribute('method'),
      defaultPreventedBefore: event.defaultPrevented,
    });

    if (!shouldUseUpcart()) {
      debugLog('submit:capture-skip-upcart-disabled');
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    debugLog('submit:capture-posting-cart-add');

    try {
      const response = await fetch(cartRoute('cart/add.js'), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });

      debugLog('submit:capture-cart-add-response', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
      });

      if (!response.ok) throw new Error('add failed');

      document.dispatchEvent(new CustomEvent('cart:updated'));
      await openUpcart();
    } catch (error) {
      debugLog('submit:capture-error', { error: String(error), stack: error?.stack });
      console.error('[upcart-integration]', error);
      form.submit();
    }
  }, true);

  if (document.body) {
    initThemeDrawerGuard();
  } else {
    document.addEventListener('DOMContentLoaded', initThemeDrawerGuard, { once: true });
  }

  window.pompeiiOpenUpcart = openUpcart;
  debugLog('script:ready');
})();
